import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import { Box, Collapse, Grid, Link, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import ZscalerZccConnectForm from "./ZscalerZccConnectForm";
import zscaler_zcc_step_1 from "./images/zscaler_zcc_step_1.png";
import zscaler_zcc_step_2 from "./images/zscaler_zcc_step_2.png";
import zscaler_zcc_step_3 from "./images/zscaler_zcc_step_3.png";
import zscaler_zcc_step_4 from "./images/zscaler_zcc_step_4.png";
import zscaler_zcc_step_5 from "./images/zscaler_zcc_step_5.png";

const ZscalerZccIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("zscaler_zcc");
  const [isCollapseOpen, setIsCollapseOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { registerZscalerZccIntegration } = useAPI();
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  const onSubmit = (values) => {
    setLoading(true);
    registerZscalerZccIntegration(values.clientId, values.secret)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Zscaler ZCC integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Zscaler ZCC and prioritize the suggested remediations."
            : ""
        }
        image={logos.ZscalerLogo}
        name={"Zscaler ZCC"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Zscaler ZCC is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}

              <GuidelineStepByStep>
                <GuidelineStep title="Add a new API key">
                  <Typography variant={"body2"}>
                    In the Zscaler Client Collector console, Go to
                    Administration → Public API.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zcc_step_1,
                        altext: "Zscaler ZCC API Keys Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>Click Add API Key.</Typography>
                  <Box mb={3}>
                    <Typography variant={"body2"}>
                      Important - Availability of the Zscaler Client Connector
                      API is limited. In case the API key is missing from <br />
                      the console - Contact the Zscaler Account team to enable
                      the API for your organization
                    </Typography>
                    <Box
                      display="flex"
                      alignItems="center"
                      mt={1}
                      onClick={() => setIsCollapseOpen(!isCollapseOpen)}
                      sx={{ cursor: "pointer" }}
                    >
                      {isCollapseOpen ? (
                        <KeyboardArrowUpRoundedIcon />
                      ) : (
                        <KeyboardArrowDownRoundedIcon />
                      )}
                      <Typography variant={"subtitle2"}>
                        Feature request template & additional information
                      </Typography>
                    </Box>
                    <Collapse in={isCollapseOpen}>
                      <Grid container direction="column" gap={2} marginLeft={3}>
                        <Typography variant={"body2"}>
                          Here is a template request format to send to Zscaler
                          Technical Account Manager(TAM) using email or <br />
                          using the Zscaler Customer portal -{" "}
                          <Link
                            href="https://zscaler.my.site.com/customers/"
                            target="_blank"
                          >
                            https://zscaler.my.site.com/customers
                          </Link>
                          .
                        </Typography>
                        <Typography variant={"body2"}>
                          "Hello, Please enable the "Public IP" feature to allow
                          API access for the Zscaler Client Connector portal.{" "}
                          <br />
                          Our tenant is at{" "}
                          {"<your Zscaler Client Connector portal URL>"}. <br />
                          Our Zscaler Company ID is{" "}
                          {
                            "<Your Company ID from ZIA portal → Administration → Company Profile>"
                          }
                          ."
                        </Typography>
                        <Typography variant={"body2"}>
                          Alternatively, Breeze Customer Success Engineer can
                          join on a call with one of Zscaler phone support
                          centers -
                          <Typography variant={"body2"}>
                            <Link
                              href="https://help.zscaler.com/contact-support"
                              target="_blank"
                            >
                              https://help.zscaler.com/contact-support
                            </Link>
                          </Typography>
                        </Typography>
                      </Grid>
                    </Collapse>
                  </Box>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zcc_step_2,
                        altext: "Zscaler ZCC add API Key",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Configure your API key">
                  <Typography variant={"body2"}>
                    In the Add API Key window: Enter a name, choose "Enable"
                    status, choose "Read" role, and set <br /> the Session
                    Validity Interval to <b>3600</b> seconds.
                    <br />
                    Click Save.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zcc_step_3,
                        altext: "Zscaler ZCC new API Key form",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    The client secret for the key displays. Copy the client
                    secret to your clipboard. And paste it in the form below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zcc_step_4,
                        altext: "Zscaler ZCC API secret",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Copy the client id">
                  <Typography variant={"body2"}>
                    In the new API row, copy the Client ID and paste it in the
                    form bellow.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zcc_step_5,
                        altext: "Zscaler ZCC Client ID",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Paste the client id and client secret">
                  <Box width={700}>
                    <ZscalerZccConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default ZscalerZccIntegration;
